// src/store/stream/agentTrigger.ts
/**
 * .tuMetrics {"tu1": 0.93, "tu2": 0.95, "tu3": 6, "notes": "Pure decisions for the agent side of sending a message: which agents to wake, what conversation they see, where the user is. No React, no supabase, no window — node-testable", "tuVersion": "2.5"}
 * .help "When a person sends a message, decides which agents (if any) should be woken, builds the short conversation they are shown, and names where the user is in the app. Pure functions; the network call lives in agentInvoke.ts."
 */
import type { ChatSession, Comment, User } from '../../types';

/* ----------------------------------------------------------------------------------------------
 * Carved out of streamStore.addStreamMessage (October 2026, readiness audit phase 2).
 *
 * The rules here encode two incidents worth remembering:
 *  - "Party crasher" (Dec 2025): an agent joined a meeting because its name appeared in prose
 *    ("$200 per person as a max" → Max shows up). Agents are woken only by explicit @mention
 *    chips or by being on a meeting's invite list. Never by text matching.
 *  - Duplicate context (Nov–Dec 2025): the optimistic copy of the message being sent was
 *    reaching the orchestrator twice. buildConversationContext() excludes it and dedupes.
 * --------------------------------------------------------------------------------------------*/

export type StreamType = Comment['streamType'];

/** Rows carrying these tags are machine payloads (help-gutter context), never shown or quoted. */
export const SYSTEM_TAGS = ['system-notification', 'context-update'];

export interface AgentSelection {
  /** The agent that shows the typing indicator and is invoked first. */
  primary: string | null;
  /** Every agent to invoke, primary first. Empty when no agent should be woken. */
  all: string[];
}

export interface SelectAgentsInput {
  streamType: StreamType;
  recipientId: string;
  mentions: string[];
  allUsers: Array<Pick<User, 'id' | 'isAgent'>>;
  sessions: Array<Pick<ChatSession, 'streamType' | 'recipientStreamId' | 'agentIds'>>;
}

/**
 * Who wakes up when this message lands.
 *  - DM to an agent → that agent.
 *  - Meeting (standup) → every agent on the invite list; an @mentioned one goes first.
 *    No invite list, no agents — even if one is @mentioned.
 *  - Team / company room → only an explicitly @mentioned agent.
 */
export function selectAgentsToInvoke(input: SelectAgentsInput): AgentSelection {
  const { streamType, recipientId, mentions, allUsers, sessions } = input;
  const isAgent = (id: string) => allUsers.some((u) => u.id === id && u.isAgent);

  if (streamType === 'direct_message') {
    return isAgent(recipientId) ? { primary: recipientId, all: [recipientId] } : { primary: null, all: [] };
  }

  if (streamType === 'standup_stream') {
    const session = sessions.find(
      (s) => s.streamType === 'standup_stream' && s.recipientStreamId === recipientId,
    );
    const invited = session?.agentIds ?? [];
    if (invited.length === 0) return { primary: null, all: [] };
    const mentioned = invited.find((id) => mentions.includes(id));
    const all = mentioned ? [mentioned, ...invited.filter((id) => id !== mentioned)] : [...invited];
    return { primary: all[0], all };
  }

  if (streamType === 'team_stream' || streamType === 'company_stream') {
    const mentioned = mentions.find(isAgent);
    return mentioned ? { primary: mentioned, all: [mentioned] } : { primary: null, all: [] };
  }

  return { primary: null, all: [] };
}

/**
 * Agents to record on the chat session after sending.
 * Meetings: exactly the invite list (never grown by mentions). Elsewhere: @mentioned agents.
 */
export function agentIdsForSession(
  input: Pick<SelectAgentsInput, 'streamType' | 'mentions' | 'allUsers'>,
  selection: AgentSelection,
): string[] {
  if (input.streamType === 'standup_stream') return selection.all;
  return input.mentions.filter((id) => input.allUsers.some((u) => u.id === id && u.isAgent));
}

/* ----------------------------------------------------------------------------------------------
 * Agent-to-agent relay (meetings)
 * --------------------------------------------------------------------------------------------*/

/** After this many agent messages in a row with no human, the agents stop talking to each other. */
export const MAX_AGENT_RUN = 6;

export interface RelayDecisionInput {
  /** The signed-in user on this client. */
  me: string;
  /** The message that just arrived over realtime. */
  arrived: Pick<Comment, 'userId' | 'streamType' | 'recipientStreamId'>;
  /** Messages of the active stream, including `arrived`, any order. */
  messages: Array<Pick<Comment, 'userId' | 'createdAt' | 'tags'>>;
  allUsers: Array<Pick<User, 'id' | 'isAgent'>>;
  sessions: Array<Pick<ChatSession, 'streamType' | 'recipientStreamId' | 'agentIds'>>;
}

export interface RelayDecision {
  relay: boolean;
  /** Other invited agents who should hear the arrived message. */
  peers: string[];
  reason:
    | 'ok'
    | 'not_agent'
    | 'not_meeting'
    | 'no_peers'
    | 'not_host'
    | 'agent_run_cap';
}

/**
 * When an agent speaks in a meeting, should *this* client wake the other invited agents?
 *
 * Every connected client receives the same realtime row, so without an election each viewer
 * would invoke every peer (N viewers → N× agent calls — the bug this replaces). The host is
 * the human who spoke most recently in the room: they started the round, their client runs it.
 * If they have left, the agents fall quiet, which is the right default.
 *
 * A run cap stops agents from ping-ponging forever once no human is in the loop.
 */
export function decideAgentRelay(input: RelayDecisionInput): RelayDecision {
  const { me, arrived, messages, allUsers, sessions } = input;
  const isAgent = (id: string) => allUsers.some((u) => u.id === id && u.isAgent);

  if (!isAgent(arrived.userId)) return { relay: false, peers: [], reason: 'not_agent' };
  if (arrived.streamType !== 'standup_stream') return { relay: false, peers: [], reason: 'not_meeting' };

  const session = sessions.find(
    (s) => s.streamType === 'standup_stream' && s.recipientStreamId === arrived.recipientStreamId,
  );
  const peers = (session?.agentIds ?? []).filter((id) => id !== arrived.userId);
  if (peers.length === 0) return { relay: false, peers: [], reason: 'no_peers' };

  const ordered = messages
    .filter((m) => !m.tags?.some((t) => SYSTEM_TAGS.includes(t)))
    .slice()
    .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

  let run = 0;
  let lastHuman: string | null = null;
  for (let i = ordered.length - 1; i >= 0; i--) {
    if (isAgent(ordered[i].userId)) {
      run++;
    } else {
      lastHuman = ordered[i].userId;
      break;
    }
  }
  if (run >= MAX_AGENT_RUN) return { relay: false, peers, reason: 'agent_run_cap' };
  if (lastHuman !== me) return { relay: false, peers, reason: 'not_host' };
  return { relay: true, peers, reason: 'ok' };
}

/* ----------------------------------------------------------------------------------------------
 * Conversation context
 * --------------------------------------------------------------------------------------------*/

export interface ConversationTurn {
  role: 'user' | 'assistant';
  content: string;
}

export interface ConversationInput {
  /** Messages already known to belong to the active stream, any order. */
  messages: Array<Pick<Comment, 'id' | 'userId' | 'content' | 'createdAt' | 'tags'>>;
  users: Array<Pick<User, 'id' | 'isAgent'>>;
  /** The message being sent right now (may already be in `messages` as an optimistic copy). */
  current: { content: string; senderId: string };
  /** Injected for tests; defaults to Date.now(). */
  now?: number;
  /** How many prior turns to include. Ten keeps stale "where I am" chatter out. */
  limit?: number;
  /** Per-turn character cap. */
  maxChars?: number;
}

/** Strip HTML and whitespace noise so two renderings of the same text compare equal. */
export function normalizeForComparison(text: string): string {
  return (text || '')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

/**
 * The short transcript an agent is shown: the last N human/agent turns in time order, minus
 * system payloads, minus the optimistic copy of the message being sent, deduped by normalized
 * text, with the current message appended last.
 */
export function buildConversationContext(input: ConversationInput): ConversationTurn[] {
  const now = input.now ?? Date.now();
  const limit = input.limit ?? 10;
  const maxChars = input.maxChars ?? 2000;
  const safe = (s: string) => (s && s.length > maxChars ? s.slice(0, maxChars) + '…' : s || '');
  const currentNorm = normalizeForComparison(input.current.content);

  const recent = input.messages
    .filter((m) => !m.tags?.some((t) => SYSTEM_TAGS.includes(t)))
    .slice(-limit)
    .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
    .filter((m) => {
      const isOptimisticCurrent =
        m.userId === input.current.senderId &&
        normalizeForComparison(m.content) === currentNorm &&
        now - new Date(m.createdAt).getTime() < 2000;
      return !isOptimisticCurrent;
    });

  const turns: ConversationTurn[] = recent.map((m) => ({
    role: input.users.find((u) => u.id === m.userId)?.isAgent ? 'assistant' : 'user',
    content: safe(m.content),
  }));
  turns.push({ role: 'user', content: safe(input.current.content) });

  const seen = new Set<string>();
  return turns.filter((t) => {
    const key = normalizeForComparison(t.content);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/* ----------------------------------------------------------------------------------------------
 * Where things are happening
 * --------------------------------------------------------------------------------------------*/

export type AgentView =
  | 'help_panel'
  | 'dm_chat'
  | 'standup_chat'
  | 'team_chat'
  | 'flow_view'
  | 'calendar_view'
  | 'tuning_max_prime'
  | 'floating_chat'
  | 'unknown';

export type UserLocation =
  | 'feed'
  | 'flow'
  | 'calendar'
  | 'plans'
  | 'roadmaps'
  | 'cards'
  | 'sprints'
  | 'projects'
  | 'directory'
  | 'features'
  | 'reports'
  | 'admin'
  | 'master_control'
  | 'devex'
  | 'guide_editor'
  | 'dashboard'
  | 'unknown';

export function isHelpSessionMessage(tags: string[] | undefined, content: string): boolean {
  return Boolean(tags?.includes('help-session')) || content.includes('🎯 Help session started');
}

/** Where the *conversation* is happening (what kind of room the agent is in). */
export function detectAgentView(input: {
  streamType: StreamType;
  tags?: string[];
  isHelpSession: boolean;
  pathname: string;
}): { view: AgentView; activity: string } {
  const { streamType, tags, isHelpSession, pathname } = input;
  if (isHelpSession) return { view: 'help_panel', activity: 'requesting_help' };
  if (tags?.includes('floating-chat')) return { view: 'floating_chat', activity: 'direct_conversation' };
  if (streamType === 'direct_message') {
    const guide = tags?.includes('guide-editor') || tags?.includes('training-guide');
    return { view: 'dm_chat', activity: guide ? 'creating_training_guide' : 'direct_conversation' };
  }
  if (streamType === 'standup_stream') return { view: 'standup_chat', activity: 'group_conversation' };
  if (streamType === 'team_stream') return { view: 'team_chat', activity: 'group_conversation' };
  if (streamType === 'company_stream') {
    if (pathname.includes('/flow')) return { view: 'flow_view', activity: 'collaborating_in_flow' };
    if (pathname.includes('/calendar')) return { view: 'calendar_view', activity: 'viewing_calendar' };
    return { view: 'team_chat', activity: 'group_conversation' };
  }
  if (pathname.includes('/master-control') && pathname.includes('/agent-management')) {
    return { view: 'tuning_max_prime', activity: 'tuning_max_prime' };
  }
  return { view: 'unknown', activity: 'general_conversation' };
}

const LOCATION_BY_PATH: Array<[string, UserLocation]> = [
  ['/dashboard', 'dashboard'],
  ['/flow', 'flow'],
  ['/calendar', 'calendar'],
  ['/plans', 'plans'],
  ['/roadmaps', 'roadmaps'],
  ['/cards', 'cards'],
  ['/sprints', 'sprints'],
  ['/projects', 'projects'],
  ['/directory', 'directory'],
  ['/features', 'features'],
  ['/reports', 'reports'],
  ['/admin', 'admin'],
  ['/master-control', 'master_control'],
  ['/devex', 'devex'],
];

/** Where the *user* is in the app (page), most specific match first; feed is the root default. */
export function detectUserLocation(pathname: string, tags?: string[]): UserLocation {
  const guideEditor =
    tags?.includes('guide-editor') || (tags?.includes('training-guide') && pathname.includes('/admin'));
  if (guideEditor) return 'guide_editor';
  for (const [needle, location] of LOCATION_BY_PATH) {
    if (pathname.includes(needle)) return location;
  }
  // The 1.0 feed and the 2.0 stream are the same place to an agent: the company's main room.
  if (pathname === '/' || pathname.startsWith('/feed') || pathname === '/2' || pathname.startsWith('/2/')) {
    return 'feed';
  }
  return 'unknown';
}
